// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  Car, Plus, Search, ChevronLeft, ChevronRight, X,
  AlertCircle, Loader2, Ban, User, Briefcase, Clock
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Vehicle Status Types
const VEHICLE_STATUSES = [
  { value: 'TENANT', label: 'Tenant', icon: User, color: '#2196F3' },
  { value: 'STAFF', label: 'Staff', icon: Briefcase, color: '#4CAF50' },
  { value: 'FORMER_STAFF', label: 'Former Staff', icon: Briefcase, color: '#9E9E9E' },
  { value: 'CONTRACTOR', label: 'Contractor', icon: Briefcase, color: '#FF9800' },
  { value: 'VISITOR', label: 'Visitor', icon: Clock, color: '#9C27B0' },
  { value: 'BANNED', label: 'Banned', icon: Ban, color: '#C8102E' }
];

// Status Badge
function StatusBadge({ status, approved }) {
  const statusConfig = VEHICLE_STATUSES.find(s => s.value === status) || VEHICLE_STATUSES[0];
  const Icon = statusConfig.icon;
  
  // Show "Pending Approval" for BANNED vehicles that aren't approved yet
  if (status === 'BANNED' && approved === false) {
    return (
      <span 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold"
        style={{ 
          backgroundColor: '#FFF3E0',
          color: '#E65100'
        }}
      >
        <Clock className="w-3.5 h-3.5" />
        Pending Approval
      </span>
    );
  }
  
  return (
    <span 
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold"
      style={{ 
        backgroundColor: `${statusConfig.color}15`,
        color: statusConfig.color
      }}
    >
      <Icon className="w-3.5 h-3.5" />
      {statusConfig.label}
    </span>
  );
}

// Table Skeleton
function TableSkeleton() {
  return (
    <tbody className="divide-y divide-gray-100">
      {[...Array(5)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4"><div className="h-6 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-6 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-40 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
        </tr>
      ))}
    </tbody>
  );
}

// Empty State
function EmptyState({ onAdd }) {
  return (
    <div className="p-12 text-center">
      <Car className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="font-medium text-gray-600">No known vehicles yet</p>
      <p className="text-sm text-gray-400 mt-1 mb-4">Add staff, tenant, or banned vehicles to your watch list</p>
      <button
        onClick={onAdd}
        className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
        style={{ backgroundColor: VCIN_COLORS.primary }}
      >
        Add Vehicle
      </button>
    </div>
  );
}

// Slide-in Panel for Add Vehicle
function AddVehiclePanel({ isOpen, onClose, sites, onSuccess }) {
  const [formData, setFormData] = useState({
    vrn: '',
    status: '',
    name: '',
    notes: '',
    site_id: '',
    valid_from: '',
    valid_to: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.vrn.trim()) newErrors.vrn = 'VRN is required';
    if (!formData.status) newErrors.status = 'Status is required';
    if (formData.status === 'BANNED' && !formData.notes.trim()) {
      newErrors.notes = 'Notes are required for banned vehicles';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        vrn: formData.vrn,
        status: formData.status,
        name: formData.name || null,
        notes: formData.notes || null,
        site_id: formData.site_id || null,
        valid_from: formData.valid_from || null,
        valid_to: formData.valid_to || null
      };
      await api.post('/known-vehicles', payload);
      onSuccess();
      onClose();
      setFormData({
        vrn: '', status: '', name: '', notes: '',
        site_id: '', valid_from: '', valid_to: ''
      });
    } catch (err) {
      setError(err.message || 'Failed to add vehicle');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div 
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto"
        data-testid="add-vehicle-panel"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add Known Vehicle</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="close-vehicle-panel-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Registration (VRN) *</label>
            <input
              type="text"
              value={formData.vrn}
              onChange={(e) => setFormData({ ...formData, vrn: e.target.value.toUpperCase() })}
              className={`w-full h-12 px-4 border rounded-lg text-lg font-bold text-center focus:outline-none focus:ring-2 ${errors.vrn ? 'border-red-300' : 'border-gray-300'}`}
              style={{ 
                backgroundColor: VCIN_COLORS.plateYellow,
                fontFamily: 'monospace',
                letterSpacing: '2px'
              }}
              placeholder="AB12 CDE"
              data-testid="vehicle-vrn"
            />
            {errors.vrn && <p className="mt-1 text-xs text-red-600">{errors.vrn}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
            <div className="grid grid-cols-2 gap-2">
              {VEHICLE_STATUSES.map(status => {
                const Icon = status.icon;
                const isSelected = formData.status === status.value;
                return (
                  <button
                    key={status.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, status: status.value })}
                    className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                      isSelected 
                        ? 'border-2' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={isSelected ? { 
                      borderColor: status.color,
                      backgroundColor: `${status.color}08`
                    } : {}}
                  >
                    <Icon 
                      className="w-4 h-4" 
                      style={{ color: isSelected ? status.color : '#9CA3AF' }}
                    />
                    <span 
                      className={`text-sm font-medium ${isSelected ? '' : 'text-gray-600'}`}
                      style={isSelected ? { color: status.color } : {}}
                    >
                      {status.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {errors.status && <p className="mt-1 text-xs text-red-600">{errors.status}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name / Description {formData.status === 'BANNED' ? '' : '(Optional)'}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              placeholder="e.g. John Smith - Flat 12B"
              data-testid="vehicle-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes {formData.status === 'BANNED' ? '*' : '(Optional)'}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 resize-none ${errors.notes ? 'border-red-300' : 'border-gray-300'}`}
              rows={3}
              placeholder={formData.status === 'BANNED' ? 'Required: Reason for ban...' : 'Additional notes...'}
              data-testid="vehicle-notes"
            />
            {errors.notes && <p className="mt-1 text-xs text-red-600">{errors.notes}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Associated Site (Optional)</label>
            <select
              value={formData.site_id}
              onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              data-testid="vehicle-site"
            >
              <option value="">All Sites</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
              <input
                type="date"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                data-testid="vehicle-valid-from"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid To</label>
              <input
                type="date"
                value={formData.valid_to}
                onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                data-testid="vehicle-valid-to"
              />
            </div>
          </div>

          {formData.status === 'BANNED' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Note:</strong> BANNED vehicles require Super Admin approval before they become active.
            </div>
          )}

          <p className="text-xs text-gray-500 pt-2">
            Vehicle data is processed under <a href="/privacy" className="underline text-blue-900">VCIN's Privacy Notice</a>.
          </p>

          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-11 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              style={{ backgroundColor: VCIN_COLORS.primary }}
              data-testid="submit-vehicle-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Vehicle'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default function KnownVehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showAddPanel, setShowAddPanel] = useState(false);

  const fetchVehicles = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 10 });
      if (statusFilter) params.append('status', statusFilter);
      
      const [vehiclesRes, sitesRes] = await Promise.all([
        api.get(`/known-vehicles?${params.toString()}`),
        api.get('/sites')
      ]);
      setVehicles(vehiclesRes.data.known_vehicles);
      setPagination(vehiclesRes.data.pagination);
      setSites(sitesRes.data.sites || []);
    } catch (err) {
      setError(err.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, [statusFilter]);

  const filteredVehicles = vehicles.filter(vehicle =>
    vehicle.vrn?.toLowerCase().includes(search.toLowerCase()) ||
    vehicle.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (error && !loading) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="known-vehicles-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Known Vehicles</h1>
        <button
          onClick={() => setShowAddPanel(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: VCIN_COLORS.primary }}
          data-testid="add-vehicle-btn"
        >
          <Plus className="w-4 h-4" />
          Add Vehicle
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by VRN or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            data-testid="vehicle-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
          data-testid="vehicle-status-filter"
        >
          <option value="">All Statuses</option>
          {VEHICLE_STATUSES.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="known-vehicles-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">VRN</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Added</th>
              </tr>
            </thead>
            
            {loading ? (
              <TableSkeleton />
            ) : filteredVehicles.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="5">
                    <EmptyState onAdd={() => setShowAddPanel(true)} />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {filteredVehicles.map((vehicle) => (
                  <tr 
                    key={vehicle.id} 
                    className={`hover:bg-gray-50 transition-colors ${
                      vehicle.status === 'BANNED' ? 'bg-red-50/30' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className="px-2 py-1 rounded text-sm font-bold"
                        style={{ 
                          backgroundColor: VCIN_COLORS.plateYellow,
                          fontFamily: 'monospace'
                        }}
                      >
                        {vehicle.vrn}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={vehicle.status} approved={vehicle.approved} />
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{vehicle.name || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 line-clamp-2">{vehicle.notes || '-'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(vehicle.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 10 + 1} to {Math.min(pagination.page * 10, pagination.total)} of {pagination.total} vehicles
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchVehicles(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => fetchVehicles(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Vehicle Panel */}
      <AddVehiclePanel
        isOpen={showAddPanel}
        onClose={() => setShowAddPanel(false)}
        sites={sites}
        onSuccess={() => fetchVehicles(1)}
      />
    </div>
  );
}
